import { Box, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { setCurrentConversationId as setCurrentConversationIdAction } from '../../actions/aiAssistant.actions';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import mainTheme from '../../assets/styles/themes/mainTheme';
import { backgroundGradients, palette, spaces } from '../../assets/styles/themes/tokens';
import AIToolsPageContent from '../../components/AIAssistant/Page/AIToolsPageContent';
import AIToolsTopBarContent from '../../components/AIAssistant/Page/AIToolsTopBarContent';
import PageTemplatePromptForm from '../../components/AIAssistant/Page/PageTemplatePromptForm';
import PodcastLoadingView from '../../components/AIAssistant/Page/PodcastLoadingView';
import { AIAssistantAlerts } from '../../components/AIAssistant/shared/AIAssistantAlerts';
import AIAssistantViewRouter, {
	type ViewMap,
} from '../../components/AIAssistant/shared/AIAssistantViewRouter';
import AIAssistantChat from '../../components/AIAssistant/shared/Chat/AIAssistantChat';
import { ActiveView } from '../../components/AIAssistant/shared/constants';
import {
	isLaunchTemplateEntitled,
	parseLaunchParams,
} from '../../components/AIAssistant/shared/templateLaunchParams';
import {
	getTemplateFormUrl,
	getTemplatePromptIdFromSlug,
} from '../../components/AIAssistant/shared/templateSlugs';
import useTemplateSelection from '../../components/AIAssistant/shared/useTemplateSelection';
import { isImageGenerationTemplate } from '../../components/AIAssistant/shared/utils';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import CircularProgressFixed from '../../components/utils/CircularProgressFixed';
import { routes } from '../../constants';
import {
	getAiGeneratedImagesRemainingLimit,
	getAiGeneratedImagesRemainingLimitStatus,
	getConversation as getConversationSelector,
	getCurrentConversationId,
} from '../../reducers/aiAssistant.selectors';
import { selectUser } from '../../reducers/user.selectors';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
	PodcastDetailsPendingReason,
} from '../../state/AIAssistantContextProvider';
import AIAssistantEditorProvider, {
	aiAssistantEditorContext,
} from '../../state/AIAssistantEditorProvider';
import { getPropagatedAIAssistantPageSource } from '../../utils/getAIAssistantPageSource';
import useAction from '../../utils/useAction';
import useRedirectIfNoAIAssistantAccess from '../../utils/useRedirectIfNoAIAssistantAccess';
import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';

const pageContainerStyles = {
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	background: backgroundGradients.aiAssistant,
	position: 'relative',
	overflowX: 'hidden',
	boxSizing: 'border-box',
};

// `ai-tools-page` is itself a scroll container: overflowX: 'hidden' makes overflow-y
// compute to 'auto', so absolutely positioned children of it scroll away with the
// content. Content-area overlays are anchored here instead — same box, never scrolls.
// `clip` rather than `hidden` because `hidden` would itself be a scroll container.
const contentAreaStyles = {
	position: 'relative',
	height: '100%',
	overflow: 'clip',
};

const breadcrumbBarStyles = {
	display: 'flex',
	padding: `${spaces.sm} ${spaces.md}`,
	alignItems: 'center',
	gap: spaces.xs,
	alignSelf: 'stretch',
	borderBottom: `1px solid ${palette.divider}`,
	background: palette.background.paperElevation2,
	position: 'sticky',
	top: 0,
};

const PAGE_VIEW_MAP: ViewMap = {
	[ActiveView.FREE_DRAFT_ENTRY]: AIToolsPageContent,
	[ActiveView.CHAT]: AIAssistantChat,
	[ActiveView.TEMPLATE_PROMPT_FORM]: PageTemplatePromptForm,
	[ActiveView.PODCAST_LOADING]: PodcastLoadingView,
};

// On wide screens the loading state renders inside the AIAssistantChat two-pane
// shell (loading view left, chat messages right) instead of the standalone
// full-screen loader.
const SPLIT_SHELL_PAGE_VIEW_MAP: ViewMap = {
	...PAGE_VIEW_MAP,
	[ActiveView.PODCAST_LOADING]: AIAssistantChat,
};

// Small screens keep the standalone loading view (SwipeableDrawer-based,
// section 7 specifics).
export const selectPageViewMap = (isSmallScreen: boolean): ViewMap =>
	isSmallScreen ? PAGE_VIEW_MAP : SPLIT_SHELL_PAGE_VIEW_MAP;

const AIToolsPageContentWrapper: React.FC = () => {
	const {
		activeView,
		displayMode,
		pendingPodcastDetailsReason,
		isPodcastEntryStatusPending,
		templateLaunchInProgressRef,
		selectedTemplatePromptId,
		setActiveView,
		setSelectedTemplatePromptId,
	} = useContext(aiAssistantContext);
	const { isTextEditorOpen } = useContext(aiAssistantEditorContext);
	const { isLoading, hasAccess } = useRedirectIfNoAIAssistantAccess();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isSmallScreen = isXsScreen || isSmScreen;
	const isXlScreen = useMediaQuery(mainTheme.breakpoints.up('xl'), { noSsr: true });
	const { router, match } = useRouter();
	const urlConversationId = match.params.conversationId;
	const setCurrentConversationId = useAction(setCurrentConversationIdAction);
	const urlConversationState = useSelector((state) =>
		getConversationSelector(state, urlConversationId ?? null)
	);
	useEffect(() => {
		if (urlConversationId && urlConversationState?.status === AsyncActionStatus.ERROR) {
			setCurrentConversationId({ conversationId: null });
			router.replace(`/${routes.aiTools.root}`);
		}
	}, [urlConversationId, urlConversationState?.status, router, setCurrentConversationId]);

	const user = useSelector(selectUser);
	const currentConversationId = useSelector(getCurrentConversationId);
	const imagesRemainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
	const imagesRemainingLimitStatus = useSelector(getAiGeneratedImagesRemainingLimitStatus);
	const { selectTemplate } = useTemplateSelection();
	const launchConsumedRef = useRef(false);
	const launchQuery = match.location?.query;

	// Consume an allowlisted dashboard launch link once on mount: pre-select the
	// template + seed the form, then strip the query so reload/back doesn't re-fire.
	// Anything invalid, unentitled, or arriving while a conversation/editor is active
	// degrades silently to the normal landing.
	useEffect(() => {
		if (launchConsumedRef.current) return;
		if (urlConversationId || currentConversationId || isTextEditorOpen) return;
		// Wait for the user before evaluating entitlement — while `me` is loading,
		// hasAccess is optimistically true and `user` is undefined, so a school user
		// would otherwise slip past the gate on a cold-load / new-tab launch.
		if (isLoading || !user) return;

		const intent = parseLaunchParams(launchQuery);
		if (!intent) return;

		const cleanUrl = `/${routes.aiTools.root}`;

		if (!isLaunchTemplateEntitled(intent.template, !!user.isSchoolUser)) {
			launchConsumedRef.current = true;
			router.replace(cleanUrl);
			return;
		}

		// Image templates gate on a remaining-limit that loads asynchronously; wait for
		// it so the launch doesn't spuriously trip the image-limit alert on a cold mount.
		// A terminal ERROR means the fetch won't resolve — stop waiting and consume the
		// launch anyway (limit stays null → no false alert) rather than stranding the card.
		if (
			isImageGenerationTemplate(intent.template) &&
			imagesRemainingLimit === null &&
			imagesRemainingLimitStatus !== AsyncActionStatus.ERROR
		)
			return;

		launchConsumedRef.current = true;
		templateLaunchInProgressRef.current = true;
		selectTemplate(intent.template, intent.prefill);
		// The strip lands on the template URL: the open form stays URL-addressable
		// (reload keeps it, back returns to the launch origin) while the prefill
		// params stay consumed-once. It must not lose the launch origin either:
		// Request Sent / Response Received resolve their page source from the
		// location at submit time, which is after this replace. Carry it in state —
		// invisible in the URL, gone on the next navigation (immediate-origin rule).
		const strippedUrl = getTemplateFormUrl(intent.template) ?? cleanUrl;
		const propagatedPageSource = getPropagatedAIAssistantPageSource(
			match.location.state,
			launchQuery
		);
		router.replace(
			propagatedPageSource
				? { pathname: strippedUrl, state: { pageSource: propagatedPageSource } }
				: strippedUrl
		);
	}, [
		urlConversationId,
		currentConversationId,
		isTextEditorOpen,
		isLoading,
		imagesRemainingLimit,
		imagesRemainingLimitStatus,
		launchQuery,
		match.location.state,
		user,
		router,
		selectTemplate,
		templateLaunchInProgressRef,
	]);

	const urlTemplateSlug = match.params.templateSlug;
	const syncedTemplateSlugRef = useRef<string | null>(null);

	// URL → form-state sync for /ki-tools/vorlage/<slug>: the slug is the
	// authoritative signal that the template form is open, so removing it (browser
	// back) closes the form. Each slug syncs once per visit — the ref keeps
	// in-flight closes (breadcrumb, Abbrechen) from re-opening a form whose state
	// teardown commits before the URL replace does.
	useEffect(() => {
		if (!urlTemplateSlug) {
			// Close only a form this sync previously acknowledged via URL: right after
			// a launch is consumed the location replace may commit later than the form
			// state, and that window must not read as "slug removed".
			if (
				syncedTemplateSlugRef.current !== null &&
				activeView === ActiveView.TEMPLATE_PROMPT_FORM &&
				!urlConversationId &&
				!currentConversationId
			) {
				setActiveView(ActiveView.FREE_DRAFT_ENTRY);
				setSelectedTemplatePromptId(null);
			}
			syncedTemplateSlugRef.current = null;
			return;
		}

		if (syncedTemplateSlugRef.current === urlTemplateSlug) return;

		const templateId = getTemplatePromptIdFromSlug(urlTemplateSlug);
		if (templateId && selectedTemplatePromptId === templateId) {
			// Already selected by a click or a consumed launch link — a second
			// selectTemplate would wipe the launch prefill.
			syncedTemplateSlugRef.current = urlTemplateSlug;
			return;
		}

		if (isLoading || !user) return;

		if (!templateId || !isLaunchTemplateEntitled(templateId, !!user.isSchoolUser)) {
			syncedTemplateSlugRef.current = urlTemplateSlug;
			router.replace(`/${routes.aiTools.root}`);
			return;
		}

		// Same async-limit wait as the launch consumer above: don't decide an image
		// template's fate while its remaining limit is still loading.
		if (
			isImageGenerationTemplate(templateId) &&
			imagesRemainingLimit === null &&
			imagesRemainingLimitStatus !== AsyncActionStatus.ERROR
		)
			return;

		syncedTemplateSlugRef.current = urlTemplateSlug;
		// Same cold-mount shield as the launch consumer: the provider's pathname sync
		// runs after this effect and would reset a freshly opened form to the landing
		// unless the in-progress ref is flipped first.
		templateLaunchInProgressRef.current = true;
		if (!selectTemplate(templateId)) {
			templateLaunchInProgressRef.current = false;
			router.replace(`/${routes.aiTools.root}`);
		}
	}, [
		urlTemplateSlug,
		urlConversationId,
		currentConversationId,
		activeView,
		selectedTemplatePromptId,
		isLoading,
		user,
		imagesRemainingLimit,
		imagesRemainingLimitStatus,
		selectTemplate,
		router,
		setActiveView,
		setSelectedTemplatePromptId,
		templateLaunchInProgressRef,
	]);

	const shouldCollapseSidebar =
		isTextEditorOpen && displayMode === AIAssistantDisplayMode.PAGE && !isSmallScreen && !isXlScreen;

	const showBreadcrumbsBar = isSmallScreen;

	if (isLoading || !hasAccess) {
		return null;
	}

	return (
		<MasterLayout
			customTopBarContent={!isSmallScreen && <AIToolsTopBarContent />}
			sidebarCollapsed={shouldCollapseSidebar}
		>
			<Box sx={contentAreaStyles} data-testid="ai-tools-content-area">
				<AIAssistantAlerts />
				<Box sx={pageContainerStyles} data-testid="ai-tools-page">
					{showBreadcrumbsBar && (
						<Box sx={(theme) => ({ ...breadcrumbBarStyles, zIndex: theme.zIndex.appBar })}>
							<AIToolsTopBarContent />
						</Box>
					)}
					{pendingPodcastDetailsReason === PodcastDetailsPendingReason.PAGE_ENTRY ||
					isPodcastEntryStatusPending ? (
						<Box
							data-testid="podcast-details-pending-loader"
							sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
						>
							<CircularProgressFixed />
						</Box>
					) : (
						<AIAssistantViewRouter activeView={activeView} viewMap={selectPageViewMap(isSmallScreen)} />
					)}
				</Box>
			</Box>
		</MasterLayout>
	);
};

const AIToolsPage: React.FC = () => {
	return (
		<AIAssistantEditorProvider>
			<AIToolsPageContentWrapper />
		</AIAssistantEditorProvider>
	);
};

export default AIToolsPage;
